/**
 * The sign-in panel, and what replaces it once there is a session.
 *
 * Task 12 shipped this as bare labelled inputs, because `packages/ui` did not exist yet. Task 14 wires
 * it to the shared primitives, and three of them earn their place here rather than being demonstrated:
 *
 * - **`Field`** generates the ids, so the label, the helper text, and a rejection message are actually
 *   associated with the input. The dashboard being replaced puts its error text in a sibling paragraph
 *   associated with nothing, so a reader focusing the field is told the label and never told why it was
 *   rejected.
 * - **`Button loading`** covers the gap between pressing "Iniciar sesión" and the answer arriving. Login
 *   is deliberately slow — bcrypt on the server — so this is the one screen in the repo where an
 *   unannounced pending state is a real problem, and a second press means a second attempt against a
 *   rate limiter that counts failures.
 * - **`DropdownMenu` and `Dialog`** carry the session's two actions, and the destructive one goes
 *   through a confirmation. That is the design's own rule: brand red is the primary *and* the
 *   destructive colour, so a destructive action can never rely on its colour alone to say what it is.
 *
 * The role is rendered and used to decide what to draw. That is the only thing a client-side role is
 * allowed to do here — the API re-derives it from the session on every request, so hiding a control is
 * a courtesy and not a control.
 */
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@apuestapr/ui';
import type { FormEvent, ReactElement } from 'react';
import { useRef, useState } from 'react';

import type { ApiClient, ApiFailure } from '../api/client.ts';
import { useSession, type SessionApi, type SessionState } from './useSession.ts';

export const failureMessage = (failure: ApiFailure): string => {
  switch (failure.kind) {
    case 'unreachable':
      return 'No pudimos conectar con la API del panel.';
    case 'http':
      switch (failure.code) {
        case 'unauthenticated':
          return 'Email o contraseña incorrectos.';
        case 'rate_limited':
          return 'Demasiados intentos. Espera unos minutos antes de volver a intentar.';
        case 'csrf_token_invalid':
          return 'La sesión del formulario expiró. Recarga la página e intenta de nuevo.';
        case 'forbidden':
          return 'Esta cuenta no tiene permiso para usar el panel.';
        default:
          return `La API respondió con un error (${String(failure.status)}).`;
      }
    case 'malformed':
      return 'La API respondió con datos que no coinciden con el contrato.';
  }
};

const LoginForm = ({
  onSubmit,
  failure,
}: {
  readonly onSubmit: (email: string, password: string) => Promise<void>;
  readonly failure: ApiFailure | undefined;
}): ReactElement => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    void onSubmit(email, password).finally(() => {
      // The component survives a rejected login — the form stays, with the message — so the pending
      // state has to be cleared on both paths rather than only on success.
      setPending(false);
    });
  };

  // The failure goes on the password field rather than into a floating alert: "Email o contraseña
  // incorrectos" is about the credential pair, and a reader moving through the form is told about it
  // where the correction happens. `Field` renders it with `role="alert"`, so it is announced when it
  // appears as well.
  const message = failure === undefined ? undefined : failureMessage(failure);

  return (
    <form onSubmit={submit} noValidate>
      <CardContent className="flex flex-col gap-4">
        <Field label="Email" required>
          {(control) => (
            <Input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              {...control}
            />
          )}
        </Field>
        <Field label="Contraseña" required {...(message === undefined ? {} : { error: message })}>
          {(control) => (
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              {...control}
            />
          )}
        </Field>
      </CardContent>
      <CardFooter>
        <Button type="submit" loading={pending} loadingLabel="Verificando las credenciales…">
          Iniciar sesión
        </Button>
      </CardFooter>
    </form>
  );
};

/**
 * Exported since Task 14.2's shell rebuild: the sidebar footer renders this directly rather than the
 * whole `AuthView` card, so "Acciones → Cerrar sesión" is reachable from every authenticated screen
 * rather than only from a session panel on the home page. `AuthView`'s own use, below, is unchanged.
 */
export const SessionActions = ({
  onRefresh,
  onLogOut,
}: {
  readonly onRefresh: () => void;
  readonly onLogOut: () => void;
}): ReactElement => {
  const [confirming, setConfirming] = useState(false);
  const actionsRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" ref={actionsRef}>
            Acciones
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>Sesión</DropdownMenuLabel>
          <DropdownMenuItem onSelect={onRefresh}>Revisar sesión</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              setConfirming(true);
            }}
          >
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent
          onCloseAutoFocus={(event) => {
            // The element the dialog would restore focus to is the menu item that opened it, and that
            // item no longer exists — the menu closed on select. Without this, closing the confirmation
            // drops focus to the document body, which is where a keyboard user loses the page. The
            // menu's trigger is the right place to land: it is what the person was on before.
            event.preventDefault();
            actionsRef.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>¿Cerrar la sesión?</DialogTitle>
            <DialogDescription>
              La sesión se revoca en el servidor y tendrás que volver a entrar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirming(false);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirming(false);
                onLogOut();
              }}
            >
              Cerrar sesión
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Split from the section so every state can be asserted without a hook or a client. */
export const AuthView = ({
  state,
  logIn,
  logOut,
  refresh,
}: Pick<SessionApi, 'state' | 'logIn' | 'logOut' | 'refresh'>): ReactElement => {
  switch (state.status) {
    case 'checking':
      return (
        <Card>
          <CardContent className="pt-6">
            <p role="status" className="m-0">
              Verificando la sesión…
            </p>
          </CardContent>
        </Card>
      );

    case 'authenticated':
      return (
        <Card>
          <CardHeader>
            <CardTitle level={3}>{state.session.user.nombre}</CardTitle>
            <CardDescription>Sesión activa en el panel.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableBody>
                <TableRow>
                  <TableHead scope="row">Rol</TableHead>
                  <TableCell data-testid="rol">{state.session.user.rol}</TableCell>
                </TableRow>
                <TableRow>
                  <TableHead scope="row">Expira</TableHead>
                  <TableCell>{state.session.expiresAt}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
          <CardFooter>
            <SessionActions onRefresh={() => void refresh()} onLogOut={() => void logOut()} />
          </CardFooter>
        </Card>
      );

    case 'anonymous':
    case 'error':
      return (
        <Card>
          <CardHeader>
            <CardTitle level={3}>Entrar al panel</CardTitle>
            <CardDescription>Solo para personal de ApuestaPR.</CardDescription>
          </CardHeader>
          <LoginForm onSubmit={logIn} failure={state.failure} />
        </Card>
      );
  }
};

export const AuthSection = ({ client }: { readonly client: ApiClient }): ReactElement => {
  const session = useSession(client);

  return (
    <section aria-labelledby="sesion">
      <h2 id="sesion">Sesión</h2>
      <AuthView
        state={session.state}
        logIn={session.logIn}
        logOut={session.logOut}
        refresh={session.refresh}
      />
    </section>
  );
};

/** Exported for the tests that render one state at a time. */
export type { SessionState };
